import React from 'react';


import IndicationCopy from './copy/safety-panel/indication';
import {
 safetyWeliregCopy,
 safetyKeytrudaQlexCopy,
} from './copy/safety-panel/safety';


import * as s from './styles';


const renderValue = (value) => (typeof value === 'function' ? value() : value);


const WELIREG_SECTION_LAYOUTS = {
 anemia: { $titleGap: '2px' },
 hypoxia: { $titleGap: '2px', $itemPaddingBottom: '4px' },
 // title→list 2px; between bullets via BulletList gap 8px
 embryo: { $titleGap: '2px' },
 // Main title + first study title share SubSection (gap 2px, pb 4px).
 // Section gap 16px between study blocks; BulletList gap 8px.
 adverse: {
   $titleGap: '16px',
   $subGap: '2px',
   $subPaddingBottom: '4px',
   $subSiblingGap: '0',
   $mainTitleInFirstSub: true,
 },
 drugInteractions: {
   $titleGap: '2px',
   $paddingBottom: '4px',
   $listGap: '16px',
 },
 lactation: { $titleGap: '2px', $paddingBottom: '4px' },
 reproductive: { $titleGap: '2px', $paddingBottom: '4px' },
 pediatricBlock: {
   $titleGap: '2px',
   $paddingBottom: '4px',
 },
};


const renderWeliregSection = (sectionObj, i) => {
 const title = sectionObj.title ? renderValue(sectionObj.title) : null;
 const {
   $subGap,
   $subPaddingBottom,
   $subSiblingGap,
   $mainTitleInFirstSub,
   ...sectionLayoutProps
 } = WELIREG_SECTION_LAYOUTS[sectionObj.layout] || {};


 return (
   <s.Section key={i} {...sectionLayoutProps}>
     {title && !$mainTitleInFirstSub && (
       <s.WeliregSectionTitle>{title}</s.WeliregSectionTitle>
     )}


     {sectionObj.subTitle ? (
       sectionObj.subTitle.map((subSectionObj, j) => (
         <s.SubSection
           key={j}
           $major={!!subSectionObj.titleHeading}
           $gap={$subGap}
           $paddingBottom={$subPaddingBottom}
           $siblingGap={$subSiblingGap}
         >
           {j === 0 && $mainTitleInFirstSub && title && (
             <s.WeliregSectionTitle>{title}</s.WeliregSectionTitle>
           )}
           {subSectionObj.titleHeading && (
             <s.WeliregSubSectionTitle>
               {renderValue(subSectionObj.titleHeading)}
             </s.WeliregSubSectionTitle>
           )}
           <s.BulletList>
             {subSectionObj.content.map((contentFn, k) => (
               <li key={k}>{renderValue(contentFn)}</li>
             ))}
           </s.BulletList>
         </s.SubSection>
       ))
     ) : (
       <s.BulletList>
         {sectionObj.items?.map((itemFn, j) => (
           <li key={j}>{renderValue(itemFn)}</li>
         ))}
       </s.BulletList>
     )}
   </s.Section>
 );
};


const KEYTRUDA_SECTION_LAYOUTS = {
 contraindications: { $titleGap: '2px', $paddingBottom: '8px' },
 standard: {
   $titleGap: '2px',
   $paddingBottom: '4px',
   $listGap: '8px',
 },
 // Title→list 2px; no section pb (parent Section gap handles spacing)
 standardTight: {
   $titleGap: '2px',
   $paddingBottom: '0',
   $listGap: '8px',
 },
 // Pneumonitis / Colitis / Hepatitis / Nephritis… — Section gap 16px
 immuneMajorSubs: {
   $titleGap: '16px',
   $subGap: '2px',
   $subPaddingBottom: '4px',
   $subSiblingGap: '0',
 },
 // Endocrinopathies → Type 1 DM — Section gap 8px
 immuneItalicSubs: {
   $titleGap: '8px',
   $subGap: '2px',
   $subPaddingBottom: '0',
   $subSiblingGap: '0',
 },
 // Hypersensitivity → HSCT — parent Section gap 8px
 sectionGap8: { $titleGap: '8px' },
};


const renderKeytrudaSection = (sectionObj, i) => {
 // Same pattern as immuneItalicSubs: parent Section gap stacks child sections
 if (sectionObj.group) {
   const groupLayout =
     KEYTRUDA_SECTION_LAYOUTS[sectionObj.layout] ||
     KEYTRUDA_SECTION_LAYOUTS.sectionGap8;


   return (
     <s.Section key={i} {...groupLayout}>
       {sectionObj.group.map((child, j) =>
         renderKeytrudaSection(child, `${i}-${j}`)
       )}
     </s.Section>
   );
 }


 const {
   $subGap,
   $subPaddingBottom,
   $subSiblingGap,
   ...sectionLayoutProps
 } =
   KEYTRUDA_SECTION_LAYOUTS[sectionObj.layout] ||
   KEYTRUDA_SECTION_LAYOUTS.standard;


 return (
   <s.Section key={i} {...sectionLayoutProps}>
     {sectionObj.title && (
       <s.SectionTitle>{sectionObj.title}</s.SectionTitle>
     )}


     {sectionObj.subTitle ? (
       sectionObj.subTitle.map((subSectionObj, j) => (
         <s.SubSection
           key={j}
           $major={!!subSectionObj.titleHeading}
           $gap={$subGap}
           $paddingBottom={$subPaddingBottom}
           $siblingGap={$subSiblingGap}
         >
           {subSectionObj.titleHeading && (
             <s.SubSectionTitle>{subSectionObj.titleHeading}</s.SubSectionTitle>
           )}
           {subSectionObj.italicHeading && (
             <s.SubSectionItalicTitle>
               {subSectionObj.italicHeading}
             </s.SubSectionItalicTitle>
           )}
           <s.KeytrudaBulletList>
             {subSectionObj.content.map((contentFn, k) => (
               <li key={k}>{renderValue(contentFn)}</li>
             ))}
           </s.KeytrudaBulletList>
         </s.SubSection>
       ))
     ) : (
       <s.KeytrudaBulletList>
         {sectionObj.items?.map((itemFn, j) => (
           <li key={j}>{renderValue(itemFn)}</li>
         ))}
       </s.KeytrudaBulletList>
     )}
   </s.Section>
 );
};


const SafetyList = ({ showSsiJobCode }) => {
 const weliregSafety = safetyWeliregCopy.mt;
 const indicationCopy = IndicationCopy.mt;
 const keytrudaSections = safetyKeytrudaQlexCopy.sections;
 const [keytrudaFirstSection, ...keytrudaRestSections] = keytrudaSections;


 return (
   <s.SafetyListContainer>
     <s.CenterColumn>
       <h6 className="top-title"> Scroll for Selected Safety Information and Indications</h6>
     </s.CenterColumn>
     <s.SafetyGrid>
       <s.ColumnsRow>
         <s.LeftColumn>
           <s.WeliregColumnIntro>
             <s.WeliregColumnTitle>
               <span className="ssi-title-lead">Selected Safety Information for</span>
               <span className="ssi-title-product">
                 WELIREG<sup>®</sup> (belzutifan) 40 mg tablets
               </span>
             </s.WeliregColumnTitle>


             <s.WarningBlock>
               <s.ColumnWarningTitle>{weliregSafety.warningList.title}</s.ColumnWarningTitle>
               <s.WarningBulletList>
                 {weliregSafety.warningList.items.map((item, i) => (
                   <li key={i}>{item}</li>
                 ))}
               </s.WarningBulletList>
             </s.WarningBlock>
           </s.WeliregColumnIntro>


           <s.WeliregColumnBody>
             {weliregSafety.sections.map(renderWeliregSection)}
           </s.WeliregColumnBody>
           <s.WeliregColumnFooter>
             <s.WeliregFinalWarningDesktop>
               {renderValue(weliregSafety.finalWarning)}
             </s.WeliregFinalWarningDesktop>
             <s.IndicationsDividerLeft>Scroll below for Indications</s.IndicationsDividerLeft>
           </s.WeliregColumnFooter>
         </s.LeftColumn>


         <s.ColumnDivider aria-hidden="true" />


         <s.RightColumn>
           <s.KeytrudaColumnIntro>
             <s.KeytrudaColumnTitle>
               <span className="ssi-title-lead">Selected Safety Information for</span>
               <span className="ssi-title-product">
                 KEYTRUDA<sup>®</sup> (pembrolizumab) Injection 100 mg and KEYTRUDA{' '}
                 <span className="purple-highlight">QLEX<sup>™</sup></span>{' '}
                 (pembrolizumab and berahyaluronidase alfa-pmph) Subcutaneous Injection
                 165 mg + 2,000 units/mL
               </span>
             </s.KeytrudaColumnTitle>
             {keytrudaFirstSection &&
               renderKeytrudaSection(keytrudaFirstSection, 0)}
           </s.KeytrudaColumnIntro>


           <s.KeytrudaColumnBody>
             {keytrudaRestSections.map((sectionObj, i) =>
               renderKeytrudaSection(sectionObj, i + 1)
             )}
           </s.KeytrudaColumnBody>
           <s.KeytrudaColumnFooter>
             <s.WeliregFinalWarningMobile>
               {renderValue(weliregSafety.finalWarning)}
             </s.WeliregFinalWarningMobile>
             <s.FinalWarningWrapper>
               {renderValue(safetyKeytrudaQlexCopy.finalWarning)}
             </s.FinalWarningWrapper>
             <s.IndicationsDividerRight>Scroll below for Indications</s.IndicationsDividerRight>
           </s.KeytrudaColumnFooter>
         </s.RightColumn>
       </s.ColumnsRow>


       <s.IndicationsFullWidth>
         {(indicationCopy.indicationtitle || indicationCopy.indicationBody) && (
           <s.IndicationSection>
             {indicationCopy.indicationtitle && (
               <s.IndicationHeading>
                 {indicationCopy.indicationtitle()}
               </s.IndicationHeading>
             )}
             {indicationCopy.indicationBody && (
               <s.IndicationBody>
                 <p>{indicationCopy.indicationBody()}</p>
               </s.IndicationBody>
             )}
           </s.IndicationSection>
         )}


         {indicationCopy.title && indicationCopy.body && (
           <s.IndicationSection>
             <s.WeliregIndicationsHeading>
               {indicationCopy.title()}
             </s.WeliregIndicationsHeading>
             <s.IndicationBody>{indicationCopy.body()}</s.IndicationBody>
           </s.IndicationSection>
         )}


         {/* {showSsiJobCode && (
           <s.SafetyJobCode>{weliregSafety.safetyJobCode}</s.SafetyJobCode>
         )} */}
       </s.IndicationsFullWidth>
     </s.SafetyGrid>
   </s.SafetyListContainer>
 );
};


export default SafetyList;
